/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Optional } from './common';
import type { EitherAnd } from './location';
import { isProvided } from '../extensible_events/utils';
import { NamespacedValue, UnstableValue } from '../utils/NamespacedValue';

/**
 * Represents the stable and unstable values of a given namespace.
 */
export type TSNamespace<N> = N extends NamespacedValue<infer S, infer U>
  ? TSNamespaceValue<S> | TSNamespaceValue<U>
  : never;

/**
 * Represents a namespaced value, if the value is a string. Used to extract provided types
 * from a TSNamespace<N> (in cases where only stable *or* unstable is provided).
 */
export type TSNamespaceValue<V> = V extends string ? V : never;

export const REFERENCE_RELATION = new NamespacedValue('m.reference');

export type AnyRelation = TSNamespace<typeof REFERENCE_RELATION> | string;

/**
 * An m.relates_to relationship
 */
export type RelatesToRelationship<R = never> = {
  'm.relates_to': {
    // See https://github.com/microsoft/TypeScript/issues/23182#issuecomment-379091887 for array syntax
    rel_type: [R] extends [never] ? AnyRelation : TSNamespace<R>;
    event_id: string;
  };
};

/**
 * An m.message event rendering
 */
export type IMessageRendering = {
  body: string;
  mimetype?: string;
};

/**
 * The namespaced value for m.text
 */
export const M_TEXT = new UnstableValue('m.text', 'org.matrix.msc1767.text');

/**
 * The namespaced value for m.message
 */
export const M_MESSAGE = new UnstableValue('m.message', 'org.matrix.msc1767.message');

/**
 * The content for an m.text event
 */
export type TextEventContent = EitherAnd<{ [M_TEXT.name]: string }, { [M_TEXT.altName]: string }>;

/**
 * The namespaced value for m.html
 */
export const M_HTML = new UnstableValue('m.html', 'org.matrix.msc1767.html');

/**
 * The content for an m.message event
 */
export type ExtensibleMessageEventContent = EitherAnd<
  { [M_MESSAGE.name]: IMessageRendering[] },
  { [M_MESSAGE.altName]: IMessageRendering[] }
>;

/**
 * Represents a potentially namespaced event type.
 */
export type ExtensibleEventType = NamespacedValue<string, string> | string;

/**
 * The content for an m.html event
 */
export type HtmlEventContent = EitherAnd<{ [M_HTML.name]: string }, { [M_HTML.altName]: string }>;

/**
 * The content for an m.message, m.text, or m.html event
 */
export type ExtensibleAnyMessageEventContent = ExtensibleMessageEventContent | TextEventContent | HtmlEventContent;

/**
 * Determines if two event types are the same, including namespaces.
 * @param given - The given event type. This will be compared
 * against the expected type.
 * @param expected - The expected event type.
 * @returns True if the given type matches the expected type.
 */
export function isEventTypeSame(
  given: Optional<ExtensibleEventType>,
  expected: Optional<ExtensibleEventType>,
): boolean {
  if (typeof given === 'string') {
    if (typeof expected === 'string') {
      return expected === given;
    } else {
      return (expected as NamespacedValue<string, string>).matches(given as string);
    }
  } else {
    if (typeof expected === 'string') {
      return (given as NamespacedValue<string, string>).matches(expected as string);
    } else {
      const expectedNs = expected as NamespacedValue<string, string>;
      const givenNs = given as NamespacedValue<string, string>;
      return expectedNs.matches(givenNs.name) || (isProvided(givenNs.altName) && expectedNs.matches(givenNs.altName!));
    }
  }
}

/**
 * Partial types for a XHubChat event.
 */
export type IPartialEvent<TContent> = {
  type: string;
  content: TContent;
};
